import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, Chip, Badge, Menu } from '@mui/material';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import { nigeriaStates } from '../../data/nigeriaMapData';
import {
  TIME_RANGE_OPTIONS,
  REGION_OPTIONS,
  CATEGORY_OPTIONS,
  ELECTION_TYPE_OPTIONS,
  VOTER_STATUS_OPTIONS,
} from './filterConstants';

// ---------------------------------------------------------------------------
// UnifiedFilter — shows relevant filter sections based on the current page
// ---------------------------------------------------------------------------

export const UnifiedFilter: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const statesList = nigeriaStates.map((s) => ({ id: s.id, name: s.name }));
  const [timeRange, setTimeRange] = React.useState('7d');
  const [selectedRegion, setSelectedRegion] = React.useState('all');
  const [newsSources, setNewsSources] = React.useState<{ name: string; isFactCheck: boolean }[]>([]);

  const path = location.pathname;
  const isCandidateProfile = path.startsWith('/candidate/');
  const showParty = path === '/candidates';
  const showElection = path === '/candidates';
  const isPoliticalActors = path === '/political-actors';
  const showState = !isCandidateProfile && !isPoliticalActors;
  const showPoliticalActorFilters = isPoliticalActors;
  const showCategory = path === '/manifestos';
  const showTimeRange = path === '/analytics' || isCandidateProfile;
  const showRegion = path === '/analytics' || isCandidateProfile;
  const showStatus = path === '/voters';
  const showSource = path === '/news';
  const showElectionType = path === '/results';
  const showHistoricalType = path === '/elections';
  const showDataInsightsTab = path === '/data-insights';
  const showForecastHistory = path === '/forecast';

  React.useEffect(() => {
    if (path !== '/news') return;
    fetch('/api/rss/sources')
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data: { name: string; isFactCheck: boolean }[]) =>
        setNewsSources(data.map((s) => ({ name: s.name, isFactCheck: s.isFactCheck }))),
      )
      .catch(() =>
        setNewsSources([
          { name: 'AllAfrica Nigeria', isFactCheck: false },
          { name: 'Arise News', isFactCheck: false },
          { name: 'BBC Africa', isFactCheck: false },
          { name: 'Blueprint', isFactCheck: false },
          { name: 'BusinessDay', isFactCheck: false },
          { name: 'Channels TV', isFactCheck: false },
          { name: 'Daily Trust', isFactCheck: false },
          { name: 'Dubawa', isFactCheck: true },
          { name: 'FactCheckHub', isFactCheck: true },
          { name: 'HumAngle', isFactCheck: false },
          { name: 'ICIR Nigeria', isFactCheck: false },
          { name: 'Leadership Newspaper', isFactCheck: false },
          { name: 'Nigerian Tribune', isFactCheck: false },
          { name: 'PM News', isFactCheck: false },
          { name: 'Peoples Gazette', isFactCheck: false },
          { name: 'Premium Times', isFactCheck: false },
          { name: 'Punch', isFactCheck: false },
          { name: 'Sahara Reporters', isFactCheck: false },
          { name: 'The Whistler', isFactCheck: false },
          { name: 'Thisday Live', isFactCheck: false },
          { name: 'YNaija', isFactCheck: false },
        ]),
      );
  }, [path]);

  const selectedState = searchParams.get('state');
  const selectedCategory = searchParams.get('category') || 'all';
  const selectedStatus = searchParams.get('status') || 'all';
  const selectedNewsSource = searchParams.get('newsSource') || '';
  const selectedParty = searchParams.get('party') || '';
  const selectedElection = searchParams.get('election') || 'all';
  const selectedElectionType = searchParams.get('electionType') || 'presidential';
  const selectedResultsYear = searchParams.get('year') || '2023';
  const showResultsYear = path === '/results';
  const newsElectionOnly = searchParams.get('electionOnly') !== '0';
  const showElectionOnly = path === '/news';
  const selectedPaRole = searchParams.get('role') || 'all';
  const selectedPaParty = searchParams.get('affiliation') || 'all';
  const selectedHistType = searchParams.get('histType') || 'All';
  const selectedDiTab = searchParams.get('diTab') || 'President';
  const selectedForecastYear = Number(searchParams.get('forecastYear') || '2023');
  const selectedForecastType = searchParams.get('forecastType') || 'presidential';

  const activeCount = [
    selectedParty ? 1 : 0,
    selectedElection !== 'all' ? 1 : 0,
    selectedState ? 1 : 0,
    isPoliticalActors && selectedPaRole !== 'all' ? 1 : 0,
    isPoliticalActors && selectedPaParty !== 'all' ? 1 : 0,
    selectedCategory !== 'all' ? 1 : 0,
    selectedStatus !== 'all' ? 1 : 0,
    timeRange !== '7d' ? 1 : 0,
    selectedRegion !== 'all' ? 1 : 0,
    selectedNewsSource ? 1 : 0,
    showElectionOnly && !newsElectionOnly ? 1 : 0,
    showElectionType && selectedElectionType !== 'presidential' ? 1 : 0,
    showResultsYear && selectedResultsYear !== '2023' ? 1 : 0,
    showHistoricalType && selectedHistType !== 'All' ? 1 : 0,
    showDataInsightsTab && selectedDiTab !== 'President' ? 1 : 0,
    showForecastHistory && selectedForecastYear !== 2023 ? 1 : 0,
    showForecastHistory && selectedForecastType !== 'presidential' ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const clearAll = () => {
    setSearchParams((p) => {
      p.delete('party');
      p.delete('election');
      p.delete('state');
      p.delete('category');
      p.delete('status');
      p.delete('newsSource');
      p.delete('electionOnly');
      p.delete('electionType');
      p.delete('role');
      p.delete('affiliation');
      p.delete('histType');
      p.delete('diTab');
      p.delete('forecastYear');
      p.delete('forecastType');
      return p;
    });
    setTimeRange('7d');
    setSelectedRegion('all');
  };

  return (
    <>
      <Badge badgeContent={activeCount} color="primary" invisible={activeCount === 0}>
        <Button
          variant={activeCount > 0 ? 'contained' : 'text'}
          startIcon={<FilterListIcon />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="medium"
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          {t('filters.filters')}
        </Button>
      </Badge>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 300,
            maxWidth: 360,
            borderRadius: 3,
            boxShadow: '0 10px 40px -10px rgba(0,0,0,0.15)',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {t('filters.filters')}
          </Typography>
          {activeCount > 0 && (
            <Button
              size="small"
              onClick={clearAll}
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                fontSize: '0.75rem',
                minWidth: 'auto',
              }}
            >
              {t('filters.clearAll')}
            </Button>
          )}
        </Box>

        {showParty && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}
            >
              Party
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {['All', 'APC', 'PDP', 'LP', 'NNPP'].map((party) => {
                const active = party === 'All' ? !selectedParty : selectedParty === party;
                return (
                  <Chip
                    key={party}
                    label={party}
                    size="small"
                    color={active ? 'primary' : 'default'}
                    variant={active ? 'filled' : 'outlined'}
                    onClick={() =>
                      setSearchParams((p) => {
                        party === 'All' ? p.delete('party') : p.set('party', party);
                        return p;
                      })
                    }
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {showElection && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}
            >
              Election Year
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {['all', '2027', '2023'].map((year) => (
                <Chip
                  key={year}
                  label={year === 'all' ? 'All Years' : year}
                  size="small"
                  color={selectedElection === year ? 'primary' : 'default'}
                  variant={selectedElection === year ? 'filled' : 'outlined'}
                  onClick={() =>
                    setSearchParams((p) => {
                      year === 'all' ? p.delete('election') : p.set('election', year);
                      return p;
                    })
                  }
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showResultsYear && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}
            >
              Election Year
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {['1999', '2003', '2007', '2011', '2015', '2019', '2023'].map((yr) => (
                <Chip
                  key={yr}
                  label={yr}
                  size="small"
                  color={selectedResultsYear === yr ? 'primary' : 'default'}
                  variant={selectedResultsYear === yr ? 'filled' : 'outlined'}
                  onClick={() => setSearchParams((p) => { p.set('year', yr); return p; })}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showElectionType && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}
            >
              Election Type
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {ELECTION_TYPE_OPTIONS.map((opt) => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  size="small"
                  color={selectedElectionType === opt.value ? 'primary' : 'default'}
                  variant={selectedElectionType === opt.value ? 'filled' : 'outlined'}
                  onClick={() =>
                    setSearchParams((p) => { p.set('electionType', opt.value); return p; })
                  }
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showState && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}
            >
              {t('filters.state')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label={t('filters.allStates')}
                size="small"
                color={!selectedState ? 'primary' : 'default'}
                variant={!selectedState ? 'filled' : 'outlined'}
                onClick={() => setSearchParams((p) => { p.delete('state'); return p; })}
                sx={{ cursor: 'pointer' }}
              />
              {statesList.map((s) => (
                <Chip
                  key={s.id}
                  label={s.name}
                  size="small"
                  color={selectedState === s.id ? 'primary' : 'default'}
                  variant={selectedState === s.id ? 'filled' : 'outlined'}
                  onClick={() => setSearchParams((p) => { p.set('state', s.id); return p; })}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showCategory && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}
            >
              {t('filters.category')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {CATEGORY_OPTIONS.map((c) => {
                const val = c === 'All' ? 'all' : c;
                const active = selectedCategory === val;
                return (
                  <Chip
                    key={c}
                    label={c}
                    size="small"
                    color={active ? 'primary' : 'default'}
                    variant={active ? 'filled' : 'outlined'}
                    onClick={() =>
                      setSearchParams((p) => {
                        val === 'all' ? p.delete('category') : p.set('category', val);
                        return p;
                      })
                    }
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {showTimeRange && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}
            >
              {t('filters.timeRange')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {TIME_RANGE_OPTIONS.map((opt) => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  size="small"
                  color={timeRange === opt.value ? 'primary' : 'default'}
                  variant={timeRange === opt.value ? 'filled' : 'outlined'}
                  onClick={() => setTimeRange(opt.value)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showRegion && (
          <Box sx={{ mb: showStatus ? 2 : 0 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}
            >
              {t('filters.region')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {REGION_OPTIONS.map((opt) => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  size="small"
                  color={selectedRegion === opt.value ? 'primary' : 'default'}
                  variant={selectedRegion === opt.value ? 'filled' : 'outlined'}
                  onClick={() => setSelectedRegion(opt.value)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showStatus && (
          <Box>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}
            >
              {t('filters.personaStatus')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {VOTER_STATUS_OPTIONS.map((opt) => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  size="small"
                  color={selectedStatus === opt.value ? 'primary' : 'default'}
                  variant={selectedStatus === opt.value ? 'filled' : 'outlined'}
                  onClick={() =>
                    setSearchParams((p) => {
                      opt.value === 'all' ? p.delete('status') : p.set('status', opt.value);
                      return p;
                    })
                  }
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showElectionOnly && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}
            >
              Content
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label="Election only"
                size="small"
                color={newsElectionOnly ? 'primary' : 'default'}
                variant={newsElectionOnly ? 'filled' : 'outlined'}
                onClick={() =>
                  setSearchParams((p) => {
                    newsElectionOnly ? p.set('electionOnly', '0') : p.delete('electionOnly');
                    return p;
                  })
                }
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                label="All topics"
                size="small"
                color={!newsElectionOnly ? 'primary' : 'default'}
                variant={!newsElectionOnly ? 'filled' : 'outlined'}
                onClick={() => setSearchParams((p) => { p.set('electionOnly', '0'); return p; })}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          </Box>
        )}

        {showSource && (
          <Box>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}
            >
              {t('filters.newsSource')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label={t('filters.allSources')}
                size="small"
                color={!selectedNewsSource ? 'primary' : 'default'}
                variant={!selectedNewsSource ? 'filled' : 'outlined'}
                onClick={() => setSearchParams((p) => { p.delete('newsSource'); return p; })}
                sx={{ cursor: 'pointer' }}
              />
              {newsSources.map((s) => (
                <Chip
                  key={s.name}
                  label={s.isFactCheck ? `${s.name} \u{1F50D}` : s.name}
                  size="small"
                  color={selectedNewsSource === s.name ? 'primary' : 'default'}
                  variant={selectedNewsSource === s.name ? 'filled' : 'outlined'}
                  onClick={() =>
                    setSearchParams((p) => {
                      selectedNewsSource === s.name
                        ? p.delete('newsSource')
                        : p.set('newsSource', s.name);
                      return p;
                    })
                  }
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showDataInsightsTab && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}
            >
              Election Type
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(['President', 'Governor', 'Senate', 'House', 'State Assembly'] as const).map(
                (label) => (
                  <Chip
                    key={label}
                    label={label}
                    size="small"
                    color={selectedDiTab === label ? 'primary' : 'default'}
                    variant={selectedDiTab === label ? 'filled' : 'outlined'}
                    onClick={() =>
                      setSearchParams((p) => {
                        label === 'President' ? p.delete('diTab') : p.set('diTab', label);
                        return p;
                      })
                    }
                    sx={{ cursor: 'pointer' }}
                  />
                ),
              )}
            </Box>
          </Box>
        )}

        {showHistoricalType && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}
            >
              Election Type
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(['All', 'Presidential', 'Senate', 'House', 'Gubernatorial'] as const).map(
                (histT) => (
                  <Chip
                    key={histT}
                    label={histT}
                    size="small"
                    color={selectedHistType === histT ? 'primary' : 'default'}
                    variant={selectedHistType === histT ? 'filled' : 'outlined'}
                    onClick={() =>
                      setSearchParams((p) => {
                        histT === 'All' ? p.delete('histType') : p.set('histType', histT);
                        return p;
                      })
                    }
                    sx={{ cursor: 'pointer' }}
                  />
                ),
              )}
            </Box>
          </Box>
        )}

        {showForecastHistory && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  display: 'block',
                  mb: 0.75,
                }}
              >
                Election Year
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {[1999, 2003, 2007, 2011, 2015, 2019, 2023].map((yr) => (
                  <Chip
                    key={yr}
                    label={yr}
                    size="small"
                    color={selectedForecastYear === yr ? 'primary' : 'default'}
                    variant={selectedForecastYear === yr ? 'filled' : 'outlined'}
                    onClick={() =>
                      setSearchParams((p) => {
                        yr === 2023
                          ? p.delete('forecastYear')
                          : p.set('forecastYear', String(yr));
                        return p;
                      })
                    }
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  display: 'block',
                  mb: 0.75,
                }}
              >
                Election Type
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {[
                  { value: 'presidential', label: 'Presidential' },
                  { value: 'gubernatorial', label: 'Gubernatorial' },
                  { value: 'senatorial', label: 'Senatorial' },
                ].map((opt) => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    size="small"
                    color={selectedForecastType === opt.value ? 'primary' : 'default'}
                    variant={selectedForecastType === opt.value ? 'filled' : 'outlined'}
                    onClick={() =>
                      setSearchParams((p) => {
                        opt.value === 'presidential'
                          ? p.delete('forecastType')
                          : p.set('forecastType', opt.value);
                        return p;
                      })
                    }
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          </>
        )}

        {showPoliticalActorFilters && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  display: 'block',
                  mb: 0.75,
                }}
              >
                Role
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {[
                  { value: 'all', label: 'All Roles' },
                  { value: 'minister', label: 'Minister' },
                  { value: 'chairman', label: 'Chairman' },
                  { value: 'commissioner', label: 'Commissioner' },
                  { value: 'spokesperson', label: 'Spokesperson' },
                  { value: 'adviser', label: 'Adviser' },
                  { value: 'secretary', label: 'Secretary' },
                  { value: 'director_general', label: 'Director-General' },
                  { value: 'committee_head', label: 'Committee Head' },
                  { value: 'chief_of_staff', label: 'Chief of Staff' },
                  { value: 'figure', label: 'Figure' },
                  { value: 'other', label: 'Other' },
                ].map((opt) => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    size="small"
                    color={selectedPaRole === opt.value ? 'primary' : 'default'}
                    variant={selectedPaRole === opt.value ? 'filled' : 'outlined'}
                    onClick={() =>
                      setSearchParams((p) => {
                        opt.value === 'all' ? p.delete('role') : p.set('role', opt.value);
                        return p;
                      })
                    }
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  display: 'block',
                  mb: 0.75,
                }}
              >
                Party
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {[
                  { value: 'all', label: 'All Parties' },
                  { value: 'APC', label: 'APC' },
                  { value: 'PDP', label: 'PDP' },
                  { value: 'LP', label: 'LP' },
                  { value: 'NNPP', label: 'NNPP' },
                  { value: 'APGA', label: 'APGA' },
                  { value: 'ADC', label: 'ADC' },
                  { value: 'SDP', label: 'SDP' },
                ].map((opt) => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    size="small"
                    color={selectedPaParty === opt.value ? 'primary' : 'default'}
                    variant={selectedPaParty === opt.value ? 'filled' : 'outlined'}
                    onClick={() =>
                      setSearchParams((p) => {
                        opt.value === 'all'
                          ? p.delete('affiliation')
                          : p.set('affiliation', opt.value);
                        return p;
                      })
                    }
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};
